import React from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import Alert from 'react-bootstrap/Alert';
import { fillPlaceholders, useNavigate, paths } from '../routing';
import ErrorMessage from '../components/error-message';
import { TextField } from '../components/fields';
import SubmitButton from '../components/submit-button';
import styles from '../pages/deals/document-template-modal/styles.module.scss';
import { createDocument } from '../utils/api/create-document-from-document-template/create-document-from-document-template';
import { useApolloClient } from '@apollo/client';
import { useNotifications } from '../notifications';
import { useDocumentState } from '../contexts/DocumentContext';
import { documentStateFromResponse } from '../utils/document-wizard-utils/document-state-from-response';
import { Button } from 'react-bootstrap';
import { TitleFormattedMessage } from '../components/titleFormattedMessage';

interface TemplateInputFormProps {
  errors: any;
  isSubmitting: boolean;
  isValid: boolean;
  onClose: () => void;
}

const CreateDocumentInputForm = ({ errors, isSubmitting, isValid, onClose }: TemplateInputFormProps) => (
  <Form noValidate>
    {errors._general && (
      <Alert variant="danger">
        <ErrorMessage>{errors._general}</ErrorMessage>
      </Alert>
    )}
    <div className={styles.createDocumentInputForm}>
      <TextField
        id="TemplateInputForm.title"
        name="title"
        type="text"
        label={false}
      />
    </div>

    <div className={styles.createDocumentButtonsGroup}>
      <Button variant="link" className="text-secondary" onClick={onClose}>
        <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
      </Button>

      <SubmitButton
        label={<FormattedMessage id="cta.create-document" defaultMessage="Create Document" />}
        disabled={isSubmitting || !isValid}
        isSubmitting={isSubmitting}
        data-testid="create-document-button"
      />
    </div>
  </Form>
);

const schema = yup.object().shape({
  title: yup.string().required(),
});

const CreateDocumentInputFormik = ({ dealId, templateId = null, onClose, isCreatingLeaseDraft, teamId }: any) => {
  const apolloClient = useApolloClient();
  const [, { error: notifyError }] = useNotifications();
  const { formatMessage } = useIntl();
  const { updateDocumentState } = useDocumentState();
  const navigate = useNavigate();

  const onSubmit = async (values: any) => {
    isCreatingLeaseDraft();
    createDocument({ dealId, templateId, title: values.title })(apolloClient)
      .then((response) => {
        const navigateUrl = fillPlaceholders(paths.documentPath, { dealId, teamId, documentId: response.id });
        //@ts-ignore
        updateDocumentState(documentStateFromResponse(response));
        navigate(navigateUrl);
      })
      .catch(() => {
        notifyError({
          message: formatMessage({
            id: 'lease-document-wizard.create-offer.error',
            defaultMessage: 'Unable to create a Document',
          }),
        });
      })
      .finally(() => {
        onClose();
      });
  };
  return (
    <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={{ title: '' }}>
      {(formikProps) => <CreateDocumentInputForm {...formikProps} onClose={onClose} />}
    </Formik>
  );
};

export default CreateDocumentInputFormik;
